import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import SortButtons from '../../../../src/components/SortButtons/index';
import { faSpinner, faUserGear } from '@fortawesome/free-solid-svg-icons';
import { useActivityContext } from '../context';
import '../../assignments/pages/AssignmentsAndActivities/assignments.scss';

const ActivityTable = ({
  data,
  nextCallback,
  skipValue,
  limit,
  totalCount,
  setSortValue,
  moreOption,
  handleSelect,
  isLoading,
  isCurrentPageSelected
}) => {

  let assign = '';
  let assignText = '';
  let assignTooltip = '';
  let circlePathColor = '';

  const { sortColumn, setSortColumn, sortDirection, setSortDirection, filters } = useActivityContext();
  const isFolderVisible = filters.folderId === -1;
  const isCourseVisible = filters.courseId === -1;
  const isBothSelected = !isFolderVisible && !isCourseVisible;

  const getColumnWidths = () => {
    if (isFolderVisible && isCourseVisible) {
      return {
        name: '20%',
        vocabulary: '12%',
        lastModified: '10%',
        lastAssigned: '10%',
        status: '14%',
        folder: '9%',
        course: '12%',
        actions: '10%'
      };
    }

    if (isFolderVisible || isCourseVisible) {
      return {
        name: '25%',
        vocabulary: '14%',
        lastModified: '10%',
        lastAssigned: '10%',
        status: '14%',
        optional: '12%',
        actions: '12%'
      };
    }

    return {
      name: '32%',
      vocabulary: '16%',
      lastModified: '13%',
      lastAssigned: '13%',
      status: '15%',
      actions: '8%'
    };
  };

  const colWidths = getColumnWidths();

  const getColSpan = () => {
    const defaultCount = 9;
    const deductions = (filters.folderId > -1 ? 1 : 0) + (filters.courseId > -1 ? 1 : 0);
    return defaultCount - deductions;
  };

  const assignmentSort = (e) => {
    let order = 0;
    let sortValue = '';
    const sortId = e.currentTarget.id;
    switch (sortId) {
    case 'sort-activity':
      sortValue = 'ActivityName';
      break;
    case 'sort-vocabularyCount':
      sortValue = 'VocabularyCount';
      break;
    case 'sort-lastModified':
      sortValue = 'Last Modified';
      break;
    case 'sort-last-assigned':
      sortValue = 'Last Assigned Date';
      break;
    case 'sort-folder':
      sortValue = 'Folder Name';
      break;
    case 'sort-course':
      sortValue = 'Course Name';
      break;
    default:
      sortValue = 'Activity';
    }
    setSortColumn(sortValue);
    if (sortColumn === sortValue) {
      order = sortColumn === sortValue && sortDirection === 'ASC' ? 1 : 0;
    } 
    setSortDirection(order === 1 ? 'DESC' : 'ASC');
    const sortdata = [sortValue, order];
    setSortValue(sortdata);
  };

  const getAssignedDetails =(assignedStatus)=>{
    switch (assignedStatus) {
    case 1:
      assign = 'badge assigned';
      assignText = 'Assigned';
      assignTooltip = 'Assigned/Accessible to all assigned students';
      break;
    case 2:
      assign = 'badge no-access';
      assignText = 'Not Accessible';
      assignTooltip = 'Assigned/Not accessible to any assigned students';
      break;
    case 3:
      assign = 'badge partial';
      assignText = 'Partially Assigned';
      assignTooltip = 'Assigned/Accessible to some assigned students';
      break;
    case 4:
      assign = 'badge random';
      assignText = 'Not Assigned';
      assignTooltip = 'Not Assigned to any students';
      break;
    default:
      assign = 'badge random';
      assignText = 'Not Assigned';
      break;
    }
  };

  const getAssignedCompletedColorPath = (percentage) =>{
    if (percentage <= 25) {
      circlePathColor = '#f84646';
    } else if (percentage <= 50) {
      circlePathColor = '#ffa800';
    } else if (percentage <= 75) {
      circlePathColor = '#3e98c7';
    } else {
      circlePathColor = '#52c9a5';
    }
  };

  return  (
    <div>
      <InfiniteScroll
        dataLength={data.length}
        next={() => nextCallback(skipValue + limit)}
        hasMore={(totalCount / (skipValue + limit)) > 1}
        scrollableTarget='scrollableDiv'>
        <div id='scrollableDiv' className='assignments-scroll-bar max-h-550 min-h-500 overflow-y'>
          <table className="table assignment-table-list">
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: colWidths.name }} />
              <col style={{ width: colWidths.vocabulary }} />
              <col style={{ width: colWidths.lastModified }} />
              <col style={{ width: colWidths.lastAssigned }} />
              <col style={{ width: colWidths.status }} />

              {isFolderVisible && <col style={{ width: colWidths.folder || colWidths.optional }} />}
              {isCourseVisible && <col style={{ width: colWidths.course || colWidths.optional }} />}

              <col style={{ width: colWidths.actions }} />
            </colgroup>
            <thead>
              <tr>
                <th className='height-unset'>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      value={isCurrentPageSelected}
                      name='allselect'
                      checked={isCurrentPageSelected}
                      onChange={handleSelect}
                      className="form-check-input"
                      id='allSelectChkBox'
                    />
                  </div>
                </th>
                <th id='sort-activity' className='height-unset cursor-pointer' onClick={(e) => assignmentSort(e)}>
                  <label className='fs-14'>Flashcard Name</label>
                  <SortButtons
                    selectedSortId={sortColumn}
                    elementSortId={'ActivityName'}
                    isAscending={sortDirection == 'ASC'}
                  />
                </th>
                <th id='sort-vocabularyCount' className="text-center height-unset cursor-pointer" onClick={(e) => assignmentSort(e)}><label className='fs-14'>{isBothSelected ? 'Vocabulary Count' : <>Vocabulary<br/>Count</>}</label>
                  <SortButtons
                    selectedSortId={sortColumn}
                    elementSortId={'VocabularyCount'}
                    isAscending={sortDirection == 'ASC'}
                  />
                </th>
                <th id='sort-lastModified' className="text-center height-unset cursor-pointer" onClick={(e) => assignmentSort(e)}><label className='fs-14'>{isBothSelected ? 'Last Modified' : <>Last<br/>Modified</>}</label>
                  <SortButtons
                    selectedSortId={sortColumn}
                    elementSortId={'Last Modified'}
                    isAscending={sortDirection == 'ASC'}
                  />
                </th>
                <th id='sort-last-assigned' className="text-center height-unset cursor-pointer" onClick={(e) => assignmentSort(e)}><label className='fs-14'>{isBothSelected ? 'Last Assigned' : <>Last<br/>Assigned</>}</label>
                  <SortButtons
                    selectedSortId={sortColumn}
                    elementSortId={'LastAssigned'}
                    isAscending={sortDirection == 'ASC'}
                  />
                </th>
                <th className='height-unset text-center'><label className='fs-14'>Status</label></th>
                {isFolderVisible && (
                  <th id='sort-folder' className='text-center height-unset cursor-pointer' onClick={(e) => assignmentSort(e)}>
                    <label className='fs-14'>Folder</label>
                    <SortButtons
                      selectedSortId={sortColumn}
                      elementSortId={'Folder'}
                      isAscending={sortDirection == 'ASC'}
                    />
                  </th>
                )}
                {isCourseVisible && (
                  <th id='sort-course' className='text-center height-unset cursor-pointer' onClick={(e) => assignmentSort(e)}>
                    <label className='fs-14'>Course</label>
                    <SortButtons
                      selectedSortId={sortColumn}
                      elementSortId={'Course'}
                      isAscending={sortDirection == 'ASC'}
                    />
                  </th>
                )}
                <th className='height-unset'></th>
              </tr>
            </thead>
            <tbody id="list-body">
              {data.map((item, index) => {
                let lastModified = new Intl.DateTimeFormat('en-US', {
                  year: '2-digit',
                  month: '2-digit',
                  day: '2-digit'
                })
                  .format(new Date(item.lastModifiedOn))
                  .split('/')
                  .join('-');
                let lastAssignedDate = item.lastAssignedDate !== null ? new Intl.DateTimeFormat('en-US', {
                  year: '2-digit',
                  month: '2-digit',
                  day: '2-digit'
                })
                  .format(new Date(item.lastAssignedDate))
                  .split('/')
                  .join('-') : '';
                let percentage = 0;
                if (item.assignedCount != 0) {
                  percentage = ((item.completedCount / item.assignedCount) * 100).toFixed(0);
                }
                getAssignedDetails(item.assignedStatus);
                getAssignedCompletedColorPath(percentage);
                return (
                  <tr key={`${item.assignmentId}-assignment-list-${index}`}>
                    <td>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          value=""
                          name={item.assignmentId}
                          checked={item?.isChecked || false}
                          onChange={handleSelect}
                          className="form-check-input"
                          id={`flexCheckDefault${item.assignmentId}`}
                        />
                      </div>
                    </td>
                    <td>                        
                      <div className='w-100 fs-14'>{item.assignmentName}</div>
                      <div className='align-items-baseline d-flex gap-2'>
                        {item.ownerName?.trim()?.length > 0 && (
                          <div>
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-top ${index}`} className="custom-tooltip">Owner of flashcard:<br/>
                                  {item.ownerName}
                                </Tooltip>
                              }>
                              <FontAwesomeIcon icon={faUserGear} className='mr-1 text-danger fs-7'/>
                            </OverlayTrigger>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-center fs-14">{item.noOfQuestion}</td>
                    <td className="text-center w-10"><label className='w-90p fs-14'>{lastModified}</label></td>
                    <td className="text-center w-10"><label className='w-90p fs-14'>{lastAssignedDate}</label></td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip id="tooltip-top" className="custom-tooltip">
                              {`${percentage}%`}
                            </Tooltip>
                          }>     
                          <div>               
                            <CircularProgressbar
                              value={percentage}
                              text={`${item.completedCount}/${item.assignedCount}`}
                              strokeWidth={12}
                              styles={buildStyles({
                                pathColor: `${circlePathColor}`,
                                textColor: '#525252',
                                trailColor: '#d6d6d6'
                              })}
                              className='h-33'
                            />
                          </div>   
                        </OverlayTrigger>
                        <div className="d-block mr-2">
                          <ReactTooltip
                            id={`status-${index}`}
                            place="right"
                            content={assignTooltip}
                            className="list-tooltip"
                          />
                          <span data-tooltip-id={`status-${index}`} className={`${assign} p-1`}>
                            {assignText}
                          </span>
                        </div>
                      </div>
                    </td>
                    {isFolderVisible && <td><label className='fs-14'>{item.folderName}</label></td>}
                    {isCourseVisible && <td className='text-center'><label className='fs-14'>{item.courseName}</label></td>}
                    <td>
                      {moreOption(item,index)}
                    </td>
                  </tr>
                );
              })}
              {isLoading &&
                <tr className='border-white'>
                  <td colSpan={getColSpan()} className='text-center fs-4'>
                    <FontAwesomeIcon icon={faSpinner} spin /><span className='ms-2'></span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </InfiniteScroll>
    </div>
  );
};

ActivityTable.propTypes = {
  data: PropTypes.array,
  nextCallback: PropTypes.func,
  skipValue: PropTypes.number,
  limit: PropTypes.number,
  totalCount: PropTypes.number,
  setSortValue: PropTypes.func,
  moreOption: PropTypes.func,
  handleSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  isCurrentPageSelected: PropTypes.bool
};
  
export default ActivityTable;
